<template>
    <b-overlay
        :show="showOverlay"
        rounded="sm"
        spinner-variant="primary"
        spinner-small
    >
        <b-row>
            <b-col>
                <div v-if="inline" class="d-flex align-bottom align-items-end">
                    <div class="w-100">
                        <b-form-group
                            :label="inputLabel"
                            label-for="name"
                            class="mb-0"
                        >
                            <vue-autosuggest
                                ref="autocomplete"
                                :suggestions="suggestions"
                                :input-props="inputProps"
                                :get-suggestion-value="getSuggestionValue"
                                @selected="onSuggestionSelect"
                                @input="searchProducts"
                            >
                                <div slot-scope="{suggestion}">
                                    {{suggestion.item.title}}
                                </div>
                            </vue-autosuggest>
                            <small class="text-danger" v-if="hasValidationError">{{ $t('Component(Label):name') }} field is required</small>
                        </b-form-group>
                    </div>
                    <div class="align-bottom pl-1">
                        <b-button
                            v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                            variant="primary"
                            @click="add"
                        >
                            {{ $t('Component(Button):add') }}
                        </b-button>
                    </div>
                </div>
                <div
                    v-else
                >
                    <validation-observer
                        ref="general"
                    >
                        <b-form-group
                            :label="inputLabel"
                            label-for="name"
                        >
                            <vue-autosuggest
                                ref="autocomplete"
                                :suggestions="suggestions"
                                :input-props="inputProps"
                                :get-suggestion-value="getSuggestionValue"
                                @selected="onSuggestionSelect"
                                @input="searchProducts"
                            >
                                <div slot-scope="{suggestion}">
                                    <span v-if="suggestion.item.productId">
                                        {{suggestion.item.productId}},
                                    </span>
                                    {{suggestion.item.title}}
                                    <span v-if="suggestion.item.supplier">
                                        <b-badge>{{suggestion.item.supplier.title}}</b-badge>
                                    </span>
                                </div>
                            </vue-autosuggest>
                            <small class="text-danger" v-if="hasValidationError">{{ $t('Component(Label):name') }} field is required</small>
                        </b-form-group>

                        <validation-provider
                            #default="{ errors }"
                            :name="$t('Component(SelectOrMakeProduct):number-of-units')"
                            rules="required|greaterThanZero|integer"
                        >
                            <b-form-group
                                :label="$t('Component(SelectOrMakeProduct):number-of-units')"
                                label-for="supplierProductName"
                            >
                                <b-form-input
                                    number
                                    :state="errors.length > 0 ? false:null"
                                    type="number"
                                    min="1"
                                    v-model="numberOfUnits"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>
                    </validation-observer>
                </div>
            </b-col>
        </b-row>
        <slot name="additional-content" />
        <b-row v-if="!inline">
            <b-col class="text-right">
                <b-button
                    v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                    variant="primary"
                    :disabled="buttonDisabled"
                    @click="add"
                >
                    {{ $t('Component(Button):add') }}
                </b-button>
            </b-col>
        </b-row>
    </b-overlay>
</template>

<script>
import { BRow, BCol, BButton, BFormGroup, BOverlay, BFormInput, BBadge } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { VueAutosuggest } from 'vue-autosuggest'
import {ProductApi} from "@core/api";
import {ProductNames, ProductType} from "@/libs/product";
import {ProductProvider} from "@core/domain/product";
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {required, integer, greaterThanZero} from '@validations'

export default {
    directives: {Ripple,},
    components: {
        BRow, BCol, BButton, BFormGroup, VueAutosuggest, BOverlay, BFormInput, ValidationProvider, ValidationObserver,
        BBadge,
    },
    props: {
        buttonDisabled: {
            default: false
        },
        searchDisabled: {
            default: false
        },
        inline: {
            type: Boolean,
            default: false
        },
        label: {
            type: String,
            default() {
                return this.$t('Component(Label):name')
            }
        },
        placeholder: {
            type: String,
            default() {
                return this.$t('Component(SelectOrMakeProduct):input-label');
            }
        },
        noLabel: {
            type: Boolean,
            default: true
        },
        modifierFunction: {
            default() {
                return (data) => data;
            }
        }
    },
    data() {
        return {
            name: null,

            numberOfUnits: 1,

            product: null,
            showOverlay: false,

            suggestions: [],
            inputProps: {
                id: 'autosuggest__input_ajax',
                placeholder: this.placeholder,
                class: 'form-control w-100',
                name: 'ajax',
            },
            hasValidationError: false,
            searchTimer: null,
            required, integer, greaterThanZero,
        };
    },
    methods: {
        searchProducts(nameOrQueryString) {
            this.name = nameOrQueryString;
            this.product = null;

            if (this.searchDisabled) {
                return;
            }

            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            this.searchTimer = setTimeout(() => {
                ProductApi.searchIncludedProducts(nameOrQueryString).then((r) => {
                    this.suggestions = [{data: r.data}];
                });
            }, 600);

            this.$emit('input', nameOrQueryString);
        },
        getSuggestionValue(suggestion) {
            return suggestion.item.title;
        },
        onSuggestionSelect(suggestion) {
            this.product = suggestion.item;
            this.name = this.product.title;

            this.$emit('selected', suggestion.item, this.numberOfUnits);
        },
        add() {
            if (this.product) {
                this.emit(this.product);
            } else {
                if (!this.name) {
                    this.hasValidationError = true;
                } else {
                    this.hasValidationError = false;
                    this.showOverlay = true;
                    //  todo move to another standalone layer
                    ProductApi.add(this.modifierFunction({
                        type: ProductType.OWN,
                        names: [{
                            key: ProductNames.OUR_PRODUCT_NAME,
                            value: this.name
                        }],
                        identifiers: []
                    })).then(r => {
                        return ProductProvider(r.data.id).getCard();
                    }).then(product => {
                        this.emit(product);
                    }).finally(() => {
                        this.showOverlay = false;
                    });
                }
            }
        },
        emit(product) {
            this.$emit('created', product, this.numberOfUnits);
            if (this.product) {
                this.product = null;
            }
            this.name = null;
        }
    },
    computed: {
        inputLabel() {
            return this.noLabel ? '' : this.label;
        }
    }
}
</script>
